import React, { memo, useCallback } from 'react';
import { Instagram, MessageCircle, Mail, Heart } from 'lucide-react';
import InteractiveLogo from './InteractiveLogo';

// WhatsApp configuration
const WHATSAPP_NUMBER = '917619546174';
const WHATSAPP_MESSAGE = 'Hello! I would like to inquire about your saree collection.';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

const Footer = memo(() => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', href: '#hero' },
    { name: 'About Us', href: '#about' },
    { name: 'Contact', href: '#contact' },
  ];

  const scrollToSection = useCallback((href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <footer className="relative bg-background pt-16 pb-8">
      {/* Decorative top border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-8 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <InteractiveLogo className="w-24 h-24 mb-6" onClick={scrollToTop} />
            <p className="font-body text-sm text-foreground/60 leading-relaxed mb-6">
              Curating timeless elegance through handcrafted sarees that celebrate India&apos;s rich textile heritage.
            </p>
            <div className="flex gap-3">
              <a
                href="https://www.instagram.com/shachi_collections"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border border-primary/30 flex items-center justify-center text-primary/70 hover:text-primary hover:border-primary hover:glow-gold transition-all duration-300"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full border border-primary/30 flex items-center justify-center text-primary/70 hover:text-primary hover:border-primary hover:glow-gold transition-all duration-300"
                aria-label="WhatsApp"
              >
                <MessageCircle className="w-5 h-5" />
              </a>
              <a
                href="mailto:shachicollections@gmail.com"
                className="w-10 h-10 rounded-full border border-primary/30 flex items-center justify-center text-primary/70 hover:text-primary hover:border-primary hover:glow-gold transition-all duration-300"
                aria-label="Email"
              >
                <Mail className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-display text-lg text-foreground mb-5">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="font-body text-sm text-foreground/60 hover:text-primary transition-colors duration-300"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-display text-lg text-foreground mb-5">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MessageCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-body text-sm text-foreground/60 hover:text-primary transition-colors"
                >
                  Chat on WhatsApp
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <a
                  href="mailto:shachicollections@gmail.com"
                  className="font-body text-sm text-foreground/60 hover:text-primary transition-colors break-all"
                >
                  shachicollections@gmail.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Instagram className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <a
                  href="https://www.instagram.com/shachi_collections"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-body text-sm text-foreground/60 hover:text-primary transition-colors"
                >
                  @shachi_collections
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-primary/10">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="font-body text-sm text-foreground/50 text-center sm:text-left">
              © {currentYear} Shachi Collections. All rights reserved.
            </p>
            <p className="font-body text-sm text-foreground/50 flex items-center gap-1">
              Crafted with <Heart className="w-4 h-4 text-primary fill-primary" /> in India
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
});

Footer.displayName = 'Footer';

export default Footer;
